import axios, { AxiosError } from 'axios';
import type {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig
} from 'axios';
import type { ApiResponse, PaginatedResponse } from '@arrowise/shared';

/**
 * API Client Configuration
 */
interface ApiClientConfig {
    baseURL: string;
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
}

/**
 * Auth callback type for handling logout
 */
type AuthLogoutCallback = () => void;
type RateLimitCallback = (info: { message: string; retryAfterMs?: number }) => void;

/**
 * API Error Class
 */
export class ApiError extends Error {
    public readonly status: number;
    public readonly data: any;
    public readonly isNetworkError: boolean;
    public readonly isRateLimited: boolean;
    public readonly isAuthError: boolean;

    constructor(
        message: string,
        status: number,
        data?: any,
        isNetworkError = false
    ) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.isNetworkError = isNetworkError;
        this.isRateLimited = status === 429;
        this.isAuthError = status === 401 || status === 403;
    }
}

/**
 * Token Management Interface
 */
interface TokenManager {
    getAccessToken(): string | null;
    getRefreshToken(): string | null;
    setTokens(accessToken: string, refreshToken: string): void;
    clearTokens(): void;
    isTokenExpired(token: string): boolean;
}

/**
 * Default Token Manager using localStorage
 */
class NoopTokenManager implements TokenManager {
    getAccessToken(): string | null { return null; }
    getRefreshToken(): string | null { return null; }
    setTokens(_accessToken: string, _refreshToken: string): void { }
    clearTokens(): void { }
    isTokenExpired(_token: string): boolean { return false; }
}

/**
 * API Client Class using Axios
 */
export class ApiClient {
    private readonly axiosInstance: AxiosInstance;
    private readonly config: ApiClientConfig;
    private readonly tokenManager: TokenManager;
    private refreshPromise: Promise<void> | null = null;
    private logoutCallback: AuthLogoutCallback | null = null;
    private rateLimitCallback: RateLimitCallback | null = null;
    private logoutCooldownUntil: number | null = null;

    constructor(
        config: Partial<ApiClientConfig> = {},
        tokenManager: TokenManager = new NoopTokenManager()
    ) {
        this.config = {
            baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
            timeout: 30000, // 30 seconds
            retryAttempts: 3,
            retryDelay: 1000, // 1 second
            ...config,
        };
        this.tokenManager = tokenManager;

        // Create axios instance
        this.axiosInstance = axios.create({
            baseURL: `${this.config.baseURL}/api`,
            timeout: this.config.timeout,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
        });

        this.setupInterceptors();
    }

    /**
     * Setup axios interceptors for auth and error handling
     */
    private setupInterceptors(): void {
        // Request interceptor (no token handling; cookies are sent automatically)
        this.axiosInstance.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => config,
            (error) => Promise.reject(error)
        );

        // Response interceptor for error handling
        this.axiosInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

                // Handle rate limits gracefully: no logout, optional gentle retry with backoff
                if (error.response?.status === 429) {
                    const retryAfterHeader = error.response.headers?.['retry-after'];
                    const retryAfterSeconds = retryAfterHeader ? parseInt(String(retryAfterHeader), 10) : NaN;
                    const retryAfterMs = !isNaN(retryAfterSeconds) ? retryAfterSeconds * 1000 : this.config.retryDelay;
                    // Notify UI if consumer registered a callback
                    if (this.rateLimitCallback) {
                        const msg = this.extractMessage(error) || 'Too many requests. Please try again shortly.';
                        this.rateLimitCallback({ message: msg, retryAfterMs });
                    }
                    // Do not retry automatically for non-idempotent methods
                    const method = (originalRequest.method || 'get').toLowerCase();
                    const safeToRetry = ['get', 'head', 'options'].includes(method);
                    if (safeToRetry && !(originalRequest as any)._rateRetry) {
                        (originalRequest as any)._rateRetry = true;
                        await new Promise((r) => setTimeout(r, retryAfterMs));
                        return this.axiosInstance(originalRequest);
                    }
                    throw this.createApiError(error);
                }

                // Auth errors: trigger logout callback (server will manage cookies)
                if (error.response?.status === 401) {
                    const now = Date.now();
                    if (!this.logoutCooldownUntil || now >= this.logoutCooldownUntil) {
                        this.triggerLogout();
                        // Cooldown to avoid repeated logout storms from concurrent 401s
                        this.logoutCooldownUntil = now + 3000;
                    }
                }

                throw this.createApiError(error);
            }
        );
    }

    // Helper to extract a readable message from error
    private extractMessage(error: AxiosError): string | undefined {
        const data = error.response?.data as any;
        if (!data) return error.message;
        return (
            data.message ||
            data.error?.message ||
            data.error ||
            data.errors?.[0]?.message ||
            data.detail
        );
    }

    /**
     * Create ApiError from AxiosError
     */
    private createApiError(error: AxiosError): ApiError {
        const isNetworkError = !error.response;
        const status = error.response?.status || 0;
        const data = error.response?.data;

        // Extract error message from various possible locations
        let message = 'An error occurred';

        if (data && typeof data === 'object') {
            const errorData = data as any;
            message =
                errorData.message ||
                errorData.error?.message ||
                errorData.error ||
                errorData.errors?.[0]?.message ||
                errorData.detail ||
                error.message;
        } else if (typeof data === 'string') {
            message = data;
        } else if (error.message) {
            message = error.message;
        }

        return new ApiError(message, status, data, isNetworkError);
    }

    /**
     * GET request
     */
    async get<T = any>(
        endpoint: string,
        params?: Record<string, any>,
        config: AxiosRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const response = await this.axiosInstance.get<ApiResponse<T>>(endpoint, {
            params,
            ...config,
        });
        return response.data;
    }

    /**
     * POST request
     */
    async post<T = any>(
        endpoint: string,
        data?: any,
        config: AxiosRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const response = await this.axiosInstance.post<ApiResponse<T>>(endpoint, data, config);
        return response.data;
    }

    /**
     * PUT request
     */
    async put<T = any>(
        endpoint: string,
        data?: any,
        config: AxiosRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const response = await this.axiosInstance.put<ApiResponse<T>>(endpoint, data, config);
        return response.data;
    }

    /**
     * PATCH request
     */
    async patch<T = any>(
        endpoint: string,
        data?: any,
        config: AxiosRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const response = await this.axiosInstance.patch<ApiResponse<T>>(endpoint, data, config);
        return response.data;
    }

    /**
     * DELETE request
     */
    async delete<T = any>(
        endpoint: string,
        config: AxiosRequestConfig = {}
    ): Promise<ApiResponse<T>> {
        const response = await this.axiosInstance.delete<ApiResponse<T>>(endpoint, config);
        return response.data;
    }

    /**
     * Set authentication tokens
     */
    setTokens(_accessToken: string, _refreshToken: string): void { }

    /**
     * Clear authentication tokens
     */
    clearTokens(): void { }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return false;
    }

    /**
     * Set logout callback for when auth errors occur
     */
    setLogoutCallback(callback: AuthLogoutCallback): void {
        this.logoutCallback = callback;
    }

    /**
     * Set rate limit callback for UI notifications
     */
    setRateLimitCallback(callback: RateLimitCallback): void {
        this.rateLimitCallback = callback;
    }

    /**
     * Trigger logout when authentication fails
     */
    private triggerLogout(): void {
        if (this.logoutCallback) {
            this.logoutCallback();
        }
    }

    /**
     * Ensure we have a valid access token
     */
    private async ensureValidToken(): Promise<void> { }

    /**
     * Refresh the access token
     */
    private async refreshAccessToken(): Promise<void> { }
}

// Create and export the default API client instance
export const apiClient = new ApiClient();

// Export types for consumers
export type { ApiResponse, PaginatedResponse, TokenManager };
